/* =============================================================
   Contracts Admin Page — /admin/contracts
   Features: Template editor (rich text), send contract to client, list all contracts
   ============================================================= */
import { useState, useCallback } from "react";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/_core/hooks/useAuth";
import { getLoginUrl } from "@/const";
import {
  FileText, Plus, Send, Trash2, Edit3, CheckCircle2, Clock, XCircle,
  Loader2, ChevronDown, ChevronUp, Copy, ExternalLink, Eye
} from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";

// ── Rich Text Editor ──────────────────────────────────────────
function RichTextEditor({ value, onChange }: { value: string; onChange: (html: string) => void }) {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      TextAlign.configure({ types: ["heading", "paragraph"] }),
    ],
    content: value,
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
  });

  if (!editor) return null;

  const btn = (active: boolean, onClick: () => void, label: string, title?: string) => (
    <button
      type="button"
      onClick={onClick}
      title={title ?? label}
      className={`px-2 py-1 text-xs rounded font-medium transition-colors ${active ? "bg-blue-600 text-white" : "bg-slate-100 hover:bg-slate-200 text-slate-700"}`}
    >
      {label}
    </button>
  );

  return (
    <div className="border border-slate-300 rounded-xl overflow-hidden">
      {/* Toolbar */}
      <div className="flex flex-wrap gap-1 p-2 bg-slate-50 border-b border-slate-200">
        {btn(editor.isActive("bold"), () => editor.chain().focus().toggleBold().run(), "B", "Тод")}
        {btn(editor.isActive("italic"), () => editor.chain().focus().toggleItalic().run(), "I", "Налуу")}
        {btn(editor.isActive("underline"), () => editor.chain().focus().toggleUnderline().run(), "U", "Доогуур зураас")}
        <div className="w-px bg-slate-300 mx-1" />
        {btn(editor.isActive("heading", { level: 1 }), () => editor.chain().focus().toggleHeading({ level: 1 }).run(), "H1")}
        {btn(editor.isActive("heading", { level: 2 }), () => editor.chain().focus().toggleHeading({ level: 2 }).run(), "H2")}
        {btn(editor.isActive("heading", { level: 3 }), () => editor.chain().focus().toggleHeading({ level: 3 }).run(), "H3")}
        <div className="w-px bg-slate-300 mx-1" />
        {btn(editor.isActive("bulletList"), () => editor.chain().focus().toggleBulletList().run(), "• Жагсаалт")}
        {btn(editor.isActive("orderedList"), () => editor.chain().focus().toggleOrderedList().run(), "1. Дугаарлалт")}
        <div className="w-px bg-slate-300 mx-1" />
        {btn(editor.isActive({ textAlign: "left" }), () => editor.chain().focus().setTextAlign("left").run(), "←")}
        {btn(editor.isActive({ textAlign: "center" }), () => editor.chain().focus().setTextAlign("center").run(), "↔")}
        {btn(editor.isActive({ textAlign: "right" }), () => editor.chain().focus().setTextAlign("right").run(), "→")}
        <div className="w-px bg-slate-300 mx-1" />
        {btn(false, () => editor.chain().focus().setHorizontalRule().run(), "─ Зураас")}
      </div>
      {/* Editor */}
      <EditorContent
        editor={editor}
        className="min-h-[300px] p-4 prose prose-sm max-w-none focus:outline-none [&_.ProseMirror]:outline-none [&_.ProseMirror]:min-h-[280px]"
      />
    </div>
  );
}

// ── Country options ───────────────────────────────────────────
const COUNTRIES = [
  { value: "usa", label: "🇺🇸 АНУ" },
  { value: "australia", label: "🇦🇺 Австрали" },
  { value: "canada", label: "🇨🇦 Канад" },
  { value: "schengen", label: "🇪🇺 Шенген" },
  { value: "ireland", label: "🇮🇪 Ирланд" },
  { value: "czech", label: "🇨🇿 Чех" },
  { value: "newzealand", label: "🇳🇿 Шинэ Зеланд" },
  { value: "other", label: "🌍 Бусад" },
];

const DEFAULT_TEMPLATE = `<h2 style="text-align:center">ҮЙЛЧИЛГЭЭНИЙ ГЭРЭЭ</h2>
<p style="text-align:center"><strong>United Dream Mongolia ХХК</strong></p>
<p style="text-align:center">СУИС-ийн урд, Gem Castle төв, 907 тоот</p>
<hr/>
<h3>1. ГЭРЭЭНИЙ ТАЛУУД</h3>
<p><strong>Үйлчилгээ үзүүлэгч:</strong> United Dream Mongolia ХХК (цаашид "Компани" гэх)</p>
<p><strong>Үйлчлүүлэгч:</strong> Гарын үсэг зурагч этгээд (цаашид "Үйлчлүүлэгч" гэх)</p>
<hr/>
<h3>2. ҮЙЛЧИЛГЭЭНИЙ АГУУЛГА</h3>
<p>Компани нь Үйлчлүүлэгчид визийн мэдүүлэг бэлтгэх, баримт бичиг боловсруулах болон холбогдох зөвлөгөө үзүүлэх үйлчилгээ үзүүлнэ.</p>
<hr/>
<h3>3. ТӨЛБӨРИЙН НӨХЦӨЛ</h3>
<p>Үйлчилгээний төлбөрийг дээрх хэсэгт заасан нөхцөлийн дагуу төлнө. Урьдчилгаа төлбөр хийгдсэнээр үйлчилгээ эхэлнэ.</p>
<hr/>
<h3>4. ТАЛУУДЫН ЭРХ, ҮҮРЭГ</h3>
<p><strong>Компанийн үүрэг:</strong></p>
<ul>
  <li>Мэргэжлийн зөвлөгөө, туслалцаа үзүүлэх</li>
  <li>Баримт бичгийг хугацаанд нь боловсруулах</li>
  <li>Үйлчлүүлэгчид мэдээлэл тогтмол хүргэх</li>
</ul>
<p><strong>Үйлчлүүлэгчийн үүрэг:</strong></p>
<ul>
  <li>Шаардлагатай баримт бичгийг цаг тухайд нь гаргаж өгөх</li>
  <li>Төлбөрийг гэрээний нөхцөлийн дагуу төлөх</li>
  <li>Үнэн зөв мэдээлэл өгөх</li>
</ul>
<hr/>
<h3>5. БУСАД НӨХЦӨЛ</h3>
<p>Энэхүү гэрээ нь цахим хэлбэрээр байгуулагдаж, хоёр талд хүчин төгөлдөр үйлчилнэ. Гарын үсэг зурсан огноо болон хугацаа автоматаар бүртгэгдэнэ.</p>`;

export default function ContractsAdminPage() {
  const { user, loading } = useAuth();
  const [activeTab, setActiveTab] = useState<"send" | "templates" | "list">("send");

  // ── Send Contract State ──
  const [sendForm, setSendForm] = useState({
    country: "usa",
    visaType: "",
    serviceName: "",
    paymentType: "full" as "full" | "installment",
    totalAmount: "",
    advanceAmount: "",
    remainingAmount: "",
    clientEmail: "",
    adminNote: "",
    bodyHtml: DEFAULT_TEMPLATE,
    templateId: undefined as number | undefined,
  });
  const [sentLink, setSentLink] = useState("");
  const [sendError, setSendError] = useState("");
  const [copied, setCopied] = useState(false);

  // ── Template State ──
  const [editingTemplate, setEditingTemplate] = useState<null | { id?: number; name: string; country: string; visaType: string; bodyHtml: string; isActive: boolean }>(null);
  const [templateError, setTemplateError] = useState("");

  // ── Queries ──
  const { data: templates, refetch: refetchTemplates } = trpc.contracts.listTemplates.useQuery();
  const { data: contractList, refetch: refetchContracts } = trpc.contracts.list.useQuery({ status: "all" });

  // ── Mutations ──
  const sendMutation = trpc.contracts.send.useMutation({
    onSuccess: (data) => {
      setSentLink(data.contractUrl);
      setSendError("");
    },
    onError: (err) => setSendError(err.message),
  });

  const saveTemplateMutation = trpc.contracts.saveTemplate.useMutation({
    onSuccess: () => { refetchTemplates(); setEditingTemplate(null); setTemplateError(""); },
    onError: (err) => setTemplateError(err.message),
  });

  const deleteTemplateMutation = trpc.contracts.deleteTemplate.useMutation({
    onSuccess: () => refetchTemplates(),
  });

  const cancelContractMutation = trpc.contracts.cancel.useMutation({
    onSuccess: () => refetchContracts(),
  });

  const handleSend = (e: React.FormEvent) => {
    e.preventDefault();
    setSendError("");
    if (!sendForm.serviceName.trim()) { setSendError("Үйлчилгээний нэр оруулна уу."); return; }
    if (!sendForm.clientEmail.trim()) { setSendError("Үйлчлүүлэгчийн имэйл оруулна уу."); return; }
    if (!sendForm.totalAmount) { setSendError("Нийт дүн оруулна уу."); return; }
    sendMutation.mutate({
      templateId: sendForm.templateId,
      country: sendForm.country,
      visaType: sendForm.visaType || undefined,
      serviceName: sendForm.serviceName,
      bodyHtml: sendForm.bodyHtml,
      paymentType: sendForm.paymentType,
      totalAmount: parseInt(sendForm.totalAmount.replace(/,/g, "")) || 0,
      advanceAmount: sendForm.paymentType === "installment" ? (parseInt(sendForm.advanceAmount.replace(/,/g, "")) || 0) : undefined,
      remainingAmount: sendForm.paymentType === "installment" ? (parseInt(sendForm.remainingAmount.replace(/,/g, "")) || 0) : undefined,
      clientEmail: sendForm.clientEmail,
      adminNote: sendForm.adminNote || undefined,
    });
  };

  const loadTemplate = (t: typeof templates extends (infer U)[] | undefined ? U : never) => {
    if (!t) return;
    setSendForm(prev => ({
      ...prev,
      country: t.country,
      visaType: t.visaType ?? "",
      bodyHtml: t.bodyHtml,
      templateId: t.id,
    }));
  };

  const copyLink = () => {
    navigator.clipboard.writeText(sentLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>;
  if (!user || user.role !== "admin") {
    window.location.href = getLoginUrl();
    return null;
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Page Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center">
            <FileText className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Гэрээний систем</h1>
            <p className="text-slate-500 text-sm">Гэрээ явуулах, загвар удирдах, гарын үсэг хянах</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-slate-200">
          {([
            { key: "send", label: "📤 Гэрээ явуулах" },
            { key: "templates", label: "📋 Загварууд" },
            { key: "list", label: `📄 Бүх гэрээ (${contractList?.length ?? 0})` },
          ] as const).map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-4 py-2.5 text-sm font-medium rounded-t-lg transition-colors -mb-px border-b-2 ${
                activeTab === tab.key
                  ? "border-blue-600 text-blue-700 bg-white"
                  : "border-transparent text-slate-500 hover:text-slate-700"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* ── TAB: Send Contract ── */}
        {activeTab === "send" && (
          <div className="space-y-6">
            {/* Load from template */}
            {templates && templates.length > 0 && (
              <div className="bg-white rounded-2xl border border-slate-200 p-5">
                <p className="text-sm font-medium text-slate-700 mb-3">Загвараас ачаалах (заавал биш):</p>
                <div className="flex flex-wrap gap-2">
                  {templates.filter(t => t.isActive).map(t => (
                    <button
                      key={t.id}
                      onClick={() => loadTemplate(t)}
                      className="px-3 py-1.5 text-xs bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200 rounded-lg transition-colors"
                    >
                      {t.name}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {sentLink ? (
              <div className="bg-white rounded-2xl border border-green-200 p-6">
                <div className="flex items-center gap-3 mb-4">
                  <CheckCircle2 className="w-8 h-8 text-green-500" />
                  <div>
                    <h3 className="font-bold text-slate-800">Гэрээ амжилттай явуулагдлаа!</h3>
                    <p className="text-sm text-slate-500">Үйлчлүүлэгчид имэйлээр линк явуулсан.</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <input
                    readOnly
                    value={sentLink}
                    className="flex-1 border border-slate-300 rounded-lg px-3 py-2 text-sm bg-slate-50 font-mono"
                  />
                  <button onClick={copyLink} className="px-4 py-2 bg-slate-100 hover:bg-slate-200 rounded-lg text-sm font-medium flex items-center gap-1.5">
                    <Copy className="w-4 h-4" />
                    {copied ? "Хуулагдлаа!" : "Хуулах"}
                  </button>
                  <a href={sentLink} target="_blank" rel="noreferrer" className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium flex items-center gap-1.5">
                    <ExternalLink className="w-4 h-4" />
                    Харах
                  </a>
                </div>
                <button
                  onClick={() => { setSentLink(""); setSendForm(prev => ({ ...prev, serviceName: "", clientEmail: "", adminNote: "", totalAmount: "", advanceAmount: "", remainingAmount: "" })); }}
                  className="mt-4 text-sm text-blue-600 hover:underline"
                >
                  + Шинэ гэрээ явуулах
                </button>
              </div>
            ) : (
              <form onSubmit={handleSend} className="space-y-5">
                <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4">
                  <h3 className="font-semibold text-slate-700">Үйлчилгээний мэдээлэл</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Улс *</label>
                      <select
                        value={sendForm.country}
                        onChange={e => setSendForm(p => ({ ...p, country: e.target.value }))}
                        className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        {COUNTRIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Виз төрөл</label>
                      <input
                        type="text"
                        value={sendForm.visaType}
                        onChange={e => setSendForm(p => ({ ...p, visaType: e.target.value }))}
                        placeholder="Жишээ нь: B1/B2, F-1"
                        className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Үйлчилгээний нэр *</label>
                    <input
                      type="text"
                      value={sendForm.serviceName}
                      onChange={e => setSendForm(p => ({ ...p, serviceName: e.target.value }))}
                      placeholder="Жишээ нь: АНУ аяллын виз (B1/B2)"
                      className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Үйлчлүүлэгчийн имэйл *</label>
                    <input
                      type="email"
                      value={sendForm.clientEmail}
                      onChange={e => setSendForm(p => ({ ...p, clientEmail: e.target.value }))}
                      placeholder="client@example.com"
                      className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                {/* Payment */}
                <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4">
                  <h3 className="font-semibold text-slate-700">Төлбөрийн мэдээлэл</h3>
                  <div className="flex gap-3">
                    {(["full", "installment"] as const).map(type => (
                      <button
                        key={type}
                        type="button"
                        onClick={() => setSendForm(p => ({ ...p, paymentType: type }))}
                        className={`flex-1 py-2.5 rounded-xl text-sm font-medium border-2 transition-colors ${
                          sendForm.paymentType === type
                            ? "border-blue-600 bg-blue-50 text-blue-700"
                            : "border-slate-200 text-slate-600 hover:border-slate-300"
                        }`}
                      >
                        {type === "full" ? "💳 Бүхлээр" : "📅 Урьдчилгаа + дараа"}
                      </button>
                    ))}
                  </div>
                  <div className={`grid gap-4 ${sendForm.paymentType === "installment" ? "grid-cols-3" : "grid-cols-1"}`}>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Нийт дүн (₮) *</label>
                      <input
                        type="text"
                        value={sendForm.totalAmount}
                        onChange={e => setSendForm(p => ({ ...p, totalAmount: e.target.value }))}
                        placeholder="850000"
                        className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    {sendForm.paymentType === "installment" && (
                      <>
                        <div>
                          <label className="block text-xs font-medium text-slate-600 mb-1">Урьдчилгаа (₮)</label>
                          <input
                            type="text"
                            value={sendForm.advanceAmount}
                            onChange={e => setSendForm(p => ({ ...p, advanceAmount: e.target.value }))}
                            placeholder="425000"
                            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-slate-600 mb-1">Үлдэгдэл (₮)</label>
                          <input
                            type="text"
                            value={sendForm.remainingAmount}
                            onChange={e => setSendForm(p => ({ ...p, remainingAmount: e.target.value }))}
                            placeholder="425000"
                            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* Contract Body */}
                <div className="bg-white rounded-2xl border border-slate-200 p-5">
                  <h3 className="font-semibold text-slate-700 mb-3">Гэрээний текст *</h3>
                  <RichTextEditor value={sendForm.bodyHtml} onChange={html => setSendForm(p => ({ ...p, bodyHtml: html }))} />
                </div>

                {/* Admin note */}
                <div className="bg-white rounded-2xl border border-slate-200 p-5">
                  <label className="block text-xs font-medium text-slate-600 mb-1">Дотоод тэмдэглэл (үйлчлүүлэгчид харагдахгүй)</label>
                  <textarea
                    value={sendForm.adminNote}
                    onChange={e => setSendForm(p => ({ ...p, adminNote: e.target.value }))}
                    rows={2}
                    placeholder="Жишээ нь: Утсаар тохиролцсон, 2024-08-01 хүртэл хүлээнэ"
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                  />
                </div>

                {sendError && (
                  <div className="flex items-center gap-2 text-red-600 bg-red-50 rounded-lg px-4 py-3 text-sm">
                    <XCircle className="w-4 h-4" /> {sendError}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={sendMutation.isPending}
                  className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-bold py-3 rounded-xl flex items-center justify-center gap-2 transition-colors"
                >
                  {sendMutation.isPending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                  Гэрээ явуулах
                </button>
              </form>
            )}
          </div>
        )}

        {/* ── TAB: Templates ── */}
        {activeTab === "templates" && (
          <div className="space-y-4">
            <button
              onClick={() => setEditingTemplate({ name: "", country: "usa", visaType: "", bodyHtml: DEFAULT_TEMPLATE, isActive: true })}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-xl transition-colors"
            >
              <Plus className="w-4 h-4" /> Шинэ загвар нэмэх
            </button>

            {editingTemplate && (
              <div className="bg-white rounded-2xl border border-blue-200 p-5 space-y-4">
                <h3 className="font-semibold text-slate-700">{editingTemplate.id ? "Загвар засах" : "Шинэ загвар"}</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Загварын нэр *</label>
                    <input
                      type="text"
                      value={editingTemplate.name}
                      onChange={e => setEditingTemplate(p => p ? { ...p, name: e.target.value } : null)}
                      placeholder="Жишээ нь: АНУ виз гэрээ"
                      className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Улс *</label>
                    <select
                      value={editingTemplate.country}
                      onChange={e => setEditingTemplate(p => p ? { ...p, country: e.target.value } : null)}
                      className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      {COUNTRIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Гэрээний текст *</label>
                  <RichTextEditor value={editingTemplate.bodyHtml} onChange={html => setEditingTemplate(p => p ? { ...p, bodyHtml: html } : null)} />
                </div>
                {templateError && <p className="text-red-600 text-sm">{templateError}</p>}
                <div className="flex gap-3">
                  <button
                    onClick={() => saveTemplateMutation.mutate({ id: editingTemplate.id, name: editingTemplate.name, country: editingTemplate.country, visaType: editingTemplate.visaType || undefined, bodyHtml: editingTemplate.bodyHtml, isActive: editingTemplate.isActive })}
                    disabled={saveTemplateMutation.isPending}
                    className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg flex items-center gap-2 disabled:opacity-50"
                  >
                    {saveTemplateMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                    Хадгалах
                  </button>
                  <button onClick={() => setEditingTemplate(null)} className="px-5 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-medium rounded-lg">
                    Болих
                  </button>
                </div>
              </div>
            )}

            {templates?.length === 0 && (
              <div className="text-center py-12 text-slate-400">
                <FileText className="w-10 h-10 mx-auto mb-2 opacity-40" />
                <p>Загвар байхгүй байна. Шинэ загвар нэмнэ үү.</p>
              </div>
            )}
            {templates?.map(t => (
              <div key={t.id} className="bg-white rounded-xl border border-slate-200 p-4 flex items-center justify-between">
                <div>
                  <p className="font-medium text-slate-800">{t.name}</p>
                  <p className="text-xs text-slate-500 mt-0.5">{COUNTRIES.find(c => c.value === t.country)?.label} {t.visaType ? `· ${t.visaType}` : ""}</p>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => setEditingTemplate({ id: t.id, name: t.name, country: t.country, visaType: t.visaType ?? "", bodyHtml: t.bodyHtml, isActive: !!t.isActive })} className="p-2 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg">
                    <Edit3 className="w-4 h-4" />
                  </button>
                  <button onClick={() => { if (confirm("Загварыг устгах уу?")) deleteTemplateMutation.mutate({ id: t.id }); }} className="p-2 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-lg">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── TAB: Contract List ── */}
        {activeTab === "list" && (
          <div className="space-y-3">
            {contractList?.length === 0 && (
              <div className="text-center py-12 text-slate-400">
                <FileText className="w-10 h-10 mx-auto mb-2 opacity-40" />
                <p>Гэрээ байхгүй байна.</p>
              </div>
            )}
            {contractList?.map(c => {
              const contractUrl = `https://dreamvisamgl.com/contract/${c.token}`;
              return (
                <div key={c.id} className="bg-white rounded-xl border border-slate-200 p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                          c.status === "signed" ? "bg-green-100 text-green-700" :
                          c.status === "cancelled" ? "bg-red-100 text-red-700" :
                          "bg-yellow-100 text-yellow-700"
                        }`}>
                          {c.status === "signed" ? <><CheckCircle2 className="w-3 h-3" /> Гарын үсэг зурагдсан</> :
                           c.status === "cancelled" ? <><XCircle className="w-3 h-3" /> Цуцлагдсан</> :
                           <><Clock className="w-3 h-3" /> Хүлээгдэж байна</>}
                        </span>
                        <span className="text-xs text-slate-400">{new Date(c.createdAt).toLocaleDateString("mn-MN")}</span>
                      </div>
                      <p className="font-medium text-slate-800">{c.serviceName}</p>
                      <p className="text-sm text-slate-500">{c.clientEmail} {c.clientName ? `· ${c.clientName}` : ""}</p>
                      <p className="text-sm font-medium text-blue-700 mt-1">
                        {c.paymentType === "full" ? `${c.totalAmount.toLocaleString()}₮ (бүхлээр)` : `${c.totalAmount.toLocaleString()}₮ · Урьдчилгаа: ${(c.advanceAmount ?? 0).toLocaleString()}₮`}
                      </p>
                    </div>
                    <div className="flex gap-1.5 flex-shrink-0">
                      <a href={contractUrl} target="_blank" rel="noreferrer" className="p-2 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg" title="Харах">
                        <Eye className="w-4 h-4" />
                      </a>
                      <button onClick={() => { navigator.clipboard.writeText(contractUrl); }} className="p-2 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg" title="Линк хуулах">
                        <Copy className="w-4 h-4" />
                      </button>
                      {c.status === "sent" && (
                        <button onClick={() => { if (confirm("Гэрээг цуцлах уу?")) cancelContractMutation.mutate({ id: c.id }); }} className="p-2 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-lg" title="Цуцлах">
                          <XCircle className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
